import { useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Edit3, Check } from "lucide-react";

const TYPES = [
  { value: "hotel", label: "Hotel" },
  { value: "bnb", label: "B&B" },
  { value: "affittacamere", label: "Affittacamere" },
  { value: "agriturismo", label: "Agriturismo" },
  { value: "campeggio", label: "Campeggio" },
  { value: "casa_vacanza", label: "Casa vacanza" },
];

const empty = { name: "", type: "hotel", star_rating: "", comune: "", provincia: "", tourist_tax_rate: 2.0, tourist_tax_max_nights: 7, exempt_age_under: 10, exempt_age_over: "" };

export default function Properties() {
  const { properties, current, select, reload } = useProperty();
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        star_rating: form.star_rating === "" ? null : Number(form.star_rating),
        tourist_tax_rate: Number(form.tourist_tax_rate),
        tourist_tax_max_nights: Number(form.tourist_tax_max_nights),
        exempt_age_under: Number(form.exempt_age_under || 10),
        exempt_age_over: form.exempt_age_over === "" ? null : Number(form.exempt_age_over),
      };
      if (editingId) await api.put(`/properties/${editingId}`, payload);
      else await api.post("/properties", payload);
      toast.success("Salvato");
      setForm(empty); setEditingId(null); setShowForm(false);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onEdit = (p) => {
    setForm({
      name: p.name, type: p.type,
      star_rating: p.star_rating ?? "",
      comune: p.comune, provincia: p.provincia || "",
      tourist_tax_rate: p.tourist_tax_rate, tourist_tax_max_nights: p.tourist_tax_max_nights,
      exempt_age_under: p.exempt_age_under ?? 10, exempt_age_over: p.exempt_age_over ?? "",
    });
    setEditingId(p.id); setShowForm(true);
  };

  const onDelete = async (id) => {
    if (!confirm("Eliminare la struttura e tutti i suoi dati?")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Eliminata"); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 md:p-12 max-w-6xl">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="text-xs tracked text-blue-700 mb-2">IL TUO ACCOUNT</p>
          <h1 className="font-heading font-semibold text-4xl tracking-tight">Le mie strutture</h1>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm(empty); }}
          data-testid="add-property"
          className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-4 py-2.5 text-sm rounded-lg hover:bg-stone-800">
          <Plus className="w-4 h-4" /> Nuova struttura
        </button>
      </div>

      {showForm && (
        <form onSubmit={onSubmit} className="border border-slate-200 bg-white p-6 mb-8 grid md:grid-cols-3 gap-4" data-testid="property-form">
          <Field label="Nome struttura"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="property-name" /></Field>
          <Field label="Tipo">
            <select value={form.type} onChange={set("type")} className={inputCls}>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Stelle (hotel)">
            <select value={form.star_rating} onChange={set("star_rating")} className={inputCls} data-testid="property-stars">
              <option value="">— Nessuna</option>
              <option value="1">★</option>
              <option value="2">★★</option>
              <option value="3">★★★</option>
              <option value="4">★★★★</option>
              <option value="5">★★★★★</option>
            </select>
          </Field>
          <Field label="Comune"><input required value={form.comune} onChange={set("comune")} className={inputCls} data-testid="property-comune" /></Field>
          <Field label="Provincia (sigla)"><input maxLength={2} value={form.provincia} onChange={(e) => setForm(f => ({ ...f, provincia: e.target.value.toUpperCase() }))} className={`${inputCls} uppercase`} /></Field>
          <Field label="Tassa / notte €"><input type="number" step="0.01" min={0} value={form.tourist_tax_rate} onChange={set("tourist_tax_rate")} className={inputCls} /></Field>
          <Field label="Max notti tassabili"><input type="number" min={1} value={form.tourist_tax_max_nights} onChange={set("tourist_tax_max_nights")} className={inputCls} /></Field>
          <Field label="Esenzione età sotto"><input type="number" min={0} max={100} value={form.exempt_age_under} onChange={set("exempt_age_under")} className={inputCls} data-testid="exempt-age-under" /></Field>
          <Field label="Esenzione età sopra (opz.)"><input type="number" min={0} max={120} value={form.exempt_age_over} onChange={set("exempt_age_over")} placeholder="es. 70" className={inputCls} /></Field>
          <div className="md:col-span-3 flex gap-2">
            <button data-testid="property-submit" className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm rounded-lg hover:bg-stone-800">Salva</button>
            <button type="button" onClick={() => { setShowForm(false); setForm(empty); setEditingId(null); }} className="text-slate-600 px-4 py-2 text-sm">Annulla</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {properties.map(p => (
          <div key={p.id} className={`border p-6 bg-white relative ${p.id === current?.id ? "border-blue-700" : "border-slate-200"}`} data-testid={`property-card-${p.id}`}>
            {p.id === current?.id && (
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2 py-1">
                <Check className="w-3 h-3" /> Attiva
              </div>
            )}
            <Building2 className="w-6 h-6 text-blue-700 mb-3" strokeWidth={1.5} />
            <h3 className="font-heading font-semibold text-2xl tracking-tight">{p.name}</h3>
            <div className="text-xs tracked text-slate-500 mt-1">
              {(TYPES.find(t => t.value === p.type)?.label) || p.type}
              {p.star_rating ? ` · ${"★".repeat(p.star_rating)}` : ""}
            </div>
            <div className="text-sm text-slate-600 mt-3">{p.comune}{p.provincia ? ` (${p.provincia})` : ""}</div>
            <div className="text-xs text-slate-500 mt-1">Tassa € {p.tourist_tax_rate?.toFixed(2)} · max {p.tourist_tax_max_nights} notti</div>
            <div className="mt-5 flex gap-2">
              {p.id !== current?.id && (
                <button onClick={() => select(p.id)} data-testid={`activate-${p.id}`}
                  className="flex-1 border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">
                  Attiva
                </button>
              )}
              <button onClick={() => onEdit(p)} className="px-3 py-2 text-sm text-slate-600 hover:text-blue-700"><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => onDelete(p.id)} className="px-3 py-2 text-sm text-slate-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white";
function Field({ label, children }) {
  return (<label className="block"><span className="text-xs text-slate-500">{label}</span><div className="mt-1">{children}</div></label>);
}
